import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Icon, RubberStamp } from "@/components/primitives";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";

// ModeratorInvite — landing page reached by tapping a "moderator_invite"
// notification. Reads the invite row by id (RLS lets the invited user
// see only their own row), renders a friendly pitch, and lets them
// approve or decline. Approve flips the row to 'accepted' — a trigger
// on moderator_invites flips profiles.is_verified_moderator → true.
// Decline flips the row to 'declined'; the notification stays in the
// inbox but no other side effects fire.

interface InviteRow {
  id: string;
  user_id: string;
  invited_by: string | null;
  status: "pending" | "accepted" | "declined" | "expired";
  message: string | null;
  created_at: string;
  responded_at: string | null;
}

const BENEFITS = [
  {
    title: "A verified stamp on your profile",
    body: "Other collectors see the FIG verified mark on every post and comment you make — instant trust signal.",
  },
  {
    title: "Help keep the feed honest",
    body: "Flag fast-fashion posts so we can move them off the vintage feed. Your reports get a different weight than a normal report.",
  },
  {
    title: "Early access to new tools",
    body: "Mod-only previews of bulk verify, comp deep-dives, and anything else we're testing before it ships to everyone.",
  },
  {
    title: "Direct line to the team",
    body: "Mods get a dedicated channel for feedback and feature requests. We listen here first.",
  },
];

export default function ModeratorInvite() {
  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [invite, setInvite] = useState<InviteRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !id) return;
    let cancelled = false;
    (async () => {
      const { data, error: qErr } = await (supabase as never as {
        from: (t: string) => {
          select: (cols: string) => {
            eq: (col: string, val: string) => {
              maybeSingle: () => Promise<{ data: InviteRow | null; error: { message: string } | null }>;
            };
          };
        };
      })
        .from("moderator_invites")
        .select("id, user_id, invited_by, status, message, created_at, responded_at")
        .eq("id", id)
        .maybeSingle();
      if (cancelled) return;
      if (qErr) {
        setError(qErr.message);
        setLoading(false);
        return;
      }
      if (!data) {
        setError("That invite doesn't exist.");
        setLoading(false);
        return;
      }
      if (data.user_id !== user.id) {
        setError("That invite isn't for you.");
        setLoading(false);
        return;
      }
      setInvite(data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id, user]);

  const respond = async (next: "accepted" | "declined") => {
    if (!invite || busy) return;
    setBusy(true);
    const { error: uErr } = await (supabase as never as {
      from: (t: string) => {
        update: (patch: Record<string, unknown>) => {
          eq: (col: string, val: string) => Promise<{ error: { message: string } | null }>;
        };
      };
    })
      .from("moderator_invites")
      .update({ status: next })
      .eq("id", invite.id);
    setBusy(false);
    if (uErr) {
      toast.error(uErr.message);
      return;
    }
    if (next === "accepted") {
      toast.success("Welcome to the verified collectors.");
      nav("/you", { replace: true });
    } else {
      toast.info("Invite dismissed.");
      nav("/notifications", { replace: true });
    }
  };

  if (loading) {
    return (
      <MobileFrame>
        <div className="paper-bg" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="caps" style={{ color: "hsl(var(--ink-2))" }}>Loading…</div>
        </div>
      </MobileFrame>
    );
  }

  if (error || !invite) {
    return (
      <MobileFrame>
        <div className="paper-bg" style={{ minHeight: "100vh", padding: "60px 24px", textAlign: "center" }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 22, color: "hsl(var(--ink))", marginBottom: 14 }}>
            {error ?? "Couldn't load this invite."}
          </div>
          <button
            onClick={() => nav("/home")}
            style={{
              padding: "12px 22px",
              background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
              border: "1px solid hsl(var(--ink))",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              letterSpacing: "0.15em", textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Go home
          </button>
        </div>
      </MobileFrame>
    );
  }

  // Already responded — show a status note instead of letting them
  // re-accept / re-decline.
  if (invite.status !== "pending") {
    return (
      <MobileFrame>
        <div className="paper-bg" style={{ minHeight: "100vh", padding: "60px 24px", textAlign: "center" }}>
          <RubberStamp
            label={invite.status === "accepted" ? "VERIFIED" : "DISMISSED"}
            sub="COLLECTOR"
            color={invite.status === "accepted" ? "hsl(var(--olive))" : "hsl(var(--ink-2))"}
          />
          <div style={{ marginTop: 24, fontFamily: "'Fraunces', serif", fontSize: 22, color: "hsl(var(--ink))" }}>
            {invite.status === "accepted"
              ? "You're already a verified collector."
              : "You dismissed this invite."}
          </div>
          <button
            onClick={() => nav(invite.status === "accepted" ? "/you" : "/home")}
            style={{
              marginTop: 22,
              padding: "12px 22px",
              background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
              border: "1px solid hsl(var(--ink))",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              letterSpacing: "0.15em", textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            {invite.status === "accepted" ? "Go to your profile" : "Back home"}
          </button>
        </div>
      </MobileFrame>
    );
  }

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", padding: "32px 20px 40px", display: "flex", flexDirection: "column", gap: 18 }}>
        {/* Back affordance */}
        <button
          onClick={() => nav(-1)}
          aria-label="Back"
          style={{
            alignSelf: "flex-start",
            background: "none", border: "1px solid hsl(var(--ink))",
            padding: 6, cursor: "pointer", color: "hsl(var(--ink))",
            width: 32, height: 32,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {Icon.chev(16, "l")}
        </button>

        {/* Hero */}
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <RubberStamp label="YOU'RE INVITED" sub="VERIFIED COLLECTOR" color="hsl(var(--rust))" />
        </div>

        <div style={{ textAlign: "center" }}>
          <div className="caps" style={{ color: "hsl(var(--rust))" }}>An invitation</div>
          <h1 style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontWeight: 900, fontStyle: "italic", fontSize: 38,
            color: "hsl(var(--ink))", letterSpacing: "-0.03em", lineHeight: 1.0,
            margin: "8px 0 0",
          }}>
            Be a verified collector.
          </h1>
          <p style={{
            marginTop: 14,
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 15, color: "hsl(var(--ink-2))", lineHeight: 1.45,
          }}>
            We picked you. Verified collectors keep FIG honest, get a stamp
            on their profile, and help shape what we build next.
          </p>
        </div>

        {/* Optional admin note */}
        {invite.message && (
          <div style={{
            padding: 14,
            border: "1px dashed hsl(var(--ink-2))",
            background: "hsl(var(--paper-2, var(--cream)))",
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 14, color: "hsl(var(--ink))", lineHeight: 1.45,
            fontStyle: "italic",
          }}>
            "{invite.message}"
            <div className="caps" style={{ color: "hsl(var(--ink-2))", marginTop: 6, fontStyle: "normal" }}>
              — note from the FIG team
            </div>
          </div>
        )}

        {/* Benefits */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 6 }}>
          <div className="caps" style={{ color: "hsl(var(--rust))" }}>What you get</div>
          {BENEFITS.map((b, i) => (
            <div
              key={b.title}
              style={{
                display: "flex", gap: 12, alignItems: "flex-start",
                padding: "12px 14px",
                border: "1px solid hsl(var(--ink-2))",
                background: "hsl(var(--cream))",
              }}
            >
              <div style={{
                flexShrink: 0,
                width: 26, height: 26, borderRadius: "50%",
                background: "hsl(var(--rust))", color: "hsl(var(--rust-fg))",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 700,
              }}>{i + 1}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 15,
                  color: "hsl(var(--ink))",
                }}>{b.title}</div>
                <div style={{
                  marginTop: 3,
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13,
                  color: "hsl(var(--ink-2))", lineHeight: 1.4,
                }}>{b.body}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ flex: 1, minHeight: 8 }} />

        {/* Actions */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            onClick={() => respond("accepted")}
            disabled={busy}
            style={{
              width: "100%", padding: "16px 18px",
              background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
              border: "1px solid hsl(var(--ink))",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
              letterSpacing: "0.18em", textTransform: "uppercase",
              cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? "…" : "Accept — make me verified"}
          </button>
          <button
            type="button"
            onClick={() => respond("declined")}
            disabled={busy}
            style={{
              width: "100%", padding: "14px 18px",
              background: "transparent", color: "hsl(var(--ink-2))",
              border: "1px dashed hsl(var(--ink-2))",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              letterSpacing: "0.15em", textTransform: "uppercase",
              cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1,
            }}
          >
            No thanks
          </button>
        </div>
      </div>
    </MobileFrame>
  );
}
